import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

/** Reset CSS injected into email iframe for consistent rendering. */
const RESET_CSS = `<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 14px;
    line-height: 1.5;
    color: #191D23;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
  img { max-width: 100%; height: auto; }
  a { color: #1a73e8; }
  /* Light DS2026 scrollbars (Gray/300 thumb, Gray/400 hover) so a wide email
     body's scrollbar matches the app's global treatment — the parent app's
     scrollbar rules don't cross the iframe boundary, so we restate them here. */
  html { scrollbar-width: thin; scrollbar-color: #D1D5DB transparent; }
  ::-webkit-scrollbar { width: 6px; height: 6px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb {
    background: #D1D5DB;
    border-radius: 9999px;
    border: 1px solid transparent;
    background-clip: padding-box;
  }
  ::-webkit-scrollbar-thumb:hover { background: #9CA3AF; background-clip: padding-box; }
  blockquote {
    border-left: 3px solid #ccc;
    padding-left: 12px;
    margin: 8px 0;
    color: #555;
  }
  pre { white-space: pre-wrap; }
</style>`

interface SrcdocResult {
  srcdoc: string
  hasExternalImages: boolean
}

/** Sanitize HTML and build the full srcdoc with reset CSS in a single parse pass. */
function buildSrcdoc(html: string, loadImages: boolean): SrcdocResult {
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')

  // Remove script tags
  doc.querySelectorAll('script').forEach((el) => el.remove())

  // Remove event handler attributes and javascript: URLs
  doc.querySelectorAll('*').forEach((el) => {
    Array.from(el.attributes).forEach((attr) => {
      if (attr.name.startsWith('on')) {
        el.removeAttribute(attr.name)
      }
      if (attr.value.trim().toLowerCase().startsWith('javascript:')) {
        el.removeAttribute(attr.name)
      }
    })
  })

  // Detect external images before potentially hiding them
  const imgs = doc.querySelectorAll('img')
  const hasExternalImages = Array.from(imgs).some((img) => {
    const src = img.getAttribute('src')
    return src && !src.startsWith('data:')
  })

  // Handle images: hide by default unless user opts in
  if (!loadImages) {
    imgs.forEach((img) => {
      const src = img.getAttribute('src')
      if (src && !src.startsWith('data:')) {
        img.setAttribute('data-original-src', src)
        img.removeAttribute('src')
        img.style.display = 'none'
      }
    })
  }

  // Inject reset CSS into head
  const head = doc.querySelector('head')
  if (head) {
    head.innerHTML = RESET_CSS + head.innerHTML
  }

  // Block external resource loading via CSP.
  // When images are opted-in, allow img-src but still block other externals
  // (CSS @import, background-image, object, embed, etc.)
  if (doc.head) {
    const meta = doc.createElement('meta')
    meta.httpEquiv = 'Content-Security-Policy'
    meta.content = loadImages
      ? "default-src 'none'; style-src 'unsafe-inline'; img-src * data:;"
      : "default-src 'none'; style-src 'unsafe-inline';"
    doc.head.prepend(meta)
  }

  return {
    srcdoc: `<!DOCTYPE html>${doc.documentElement.outerHTML}`,
    hasExternalImages,
  }
}

interface SafeEmailRendererProps {
  html: string
}

/** Renders email HTML inside a sandboxed iframe for security. */
export function SafeEmailRenderer({ html }: SafeEmailRendererProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [height, setHeight] = useState(100)
  const [loadImages, setLoadImages] = useState(false)
  const observerRef = useRef<ResizeObserver | null>(null)

  const { srcdoc, hasExternalImages } = useMemo(
    () => buildSrcdoc(html, loadImages),
    [html, loadImages]
  )

  const updateHeight = useCallback(() => {
    const iframe = iframeRef.current
    if (!iframe?.contentDocument?.body) return
    const scrollHeight = iframe.contentDocument.body.scrollHeight
    if (scrollHeight > 0) {
      setHeight(scrollHeight + 2)
    }
  }, [])

  const handleLoad = useCallback(() => {
    const iframe = iframeRef.current
    if (!iframe?.contentDocument?.body) return

    updateHeight()

    // Observe body resize for dynamic content
    observerRef.current?.disconnect()
    const observer = new ResizeObserver(updateHeight)
    observer.observe(iframe.contentDocument.body)
    observerRef.current = observer
  }, [updateHeight])

  useEffect(() => {
    return () => observerRef.current?.disconnect()
  }, [])

  return (
    <div>
      <iframe
        ref={iframeRef}
        sandbox="allow-same-origin"
        srcDoc={srcdoc}
        onLoad={handleLoad}
        style={{ width: '100%', height, border: 'none', overflow: 'hidden' }}
        title="Email content"
      />
      {hasExternalImages && !loadImages && (
        <button
          type="button"
          onClick={() => setLoadImages(true)}
          className="mt-1 text-xs text-[#1a73e8] hover:underline"
        >
          Load images
        </button>
      )}
    </div>
  )
}
